import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, getDoc, arrayRemove } from 'firebase/firestore';
import { db, functions as cloudFunctions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '@/contexts/AuthContext';
import { useUnit } from '@/contexts/UnitContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/layout/Header';
import ProfileSetup from '@/components/auth/ProfileSetup';
import TwoFactorSetup from '@/components/auth/TwoFactorSetup';
import { showSuccess, showError, confirmAction } from '@/lib/swal';
import { formatDate, cn } from '@/lib/utils';
import { Unit, TrustedDevice, UnitType } from '@/types';
import { unitDisplaysNumber } from '@/lib/unit-number-display';
import { User, Shield, ChevronLeft, LogOut, Smartphone, Monitor, Trash2, Building, Mail, Plus, UserPlus } from 'lucide-react';

type TabType = 'profile' | 'units' | 'guests' | 'security';

export default function MySettingsPage() {
  const { currentUser, userProfile, refreshUserProfile } = useAuth();
  const { refreshUnits } = useUnit();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [saving, setSaving] = useState(false);
  const [unitDetails, setUnitDetails] = useState<Record<string, Unit>>({});
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [guestSubscriptionUnits, setGuestSubscriptionUnits] = useState<Record<string, Unit>>({});
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);

  // Security settings state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [showVerifyDisable, setShowVerifyDisable] = useState(false);

  // Email change state
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [changingEmail, setChangingEmail] = useState(false);

  // Fetch unit details for each membership
  useEffect(() => {
    const fetchUnitDetails = async () => {
      const memberships = userProfile?.units || userProfile?.units;
      if (!memberships || memberships.length === 0) return;

      setLoadingUnits(true);
      try {
        const details: Record<string, Unit> = {};
        
        for (const membership of memberships) {
          const unitId = 'unitId' in membership ? membership.unitId : (membership as any).lodgeId;
          const unitDoc = await getDoc(doc(db, 'units', unitId));
          if (unitDoc.exists()) {
            const data = unitDoc.data();
            details[unitId] = {
              id: unitDoc.id,
              name: data.name,
              unitNumber: data.unitNumber || data.lodgeNumber || data.number || '',
              lodgeNumber: data.lodgeNumber,
              members: data.members || [],
              createdAt: data.createdAt?.toDate() || new Date(),
              subscription: data.subscription || { status: 'trial', billingEmail: '' },
              settings: data.settings || {},
              typeId: data.typeId || '',
            };
          }
        }
        
        setUnitDetails(details);
      } catch (error) {
        console.error('Error fetching unit details:', error);
      } finally {
        setLoadingUnits(false);
      }
    };

    fetchUnitDetails();
  }, [userProfile]);

  // Fetch unit details for guest subscriptions
  useEffect(() => {
    const fetchGuestSubscriptionUnits = async () => {
      if (!userProfile?.guestSubscriptions || userProfile.guestSubscriptions.length === 0) {
        setGuestSubscriptionUnits({});
        return;
      }

      try {
        const details: Record<string, Unit> = {};
        
        for (const subscription of userProfile.guestSubscriptions) {
          const unitDoc = await getDoc(doc(db, 'units', subscription.unitId));
          if (unitDoc.exists()) {
            const data = unitDoc.data();
            details[subscription.unitId] = {
              id: unitDoc.id,
              name: data.name,
              unitNumber: data.unitNumber || data.lodgeNumber || data.number || '',
              lodgeNumber: data.lodgeNumber,
              members: data.members || [],
              createdAt: data.createdAt?.toDate() || new Date(),
              subscription: data.subscription || { status: 'trial', billingEmail: '' },
              settings: data.settings || {},
              typeId: data.typeId || '',
            };
          }
        }
        
        setGuestSubscriptionUnits(details);
      } catch (error) {
        console.error('Error fetching guest subscription units:', error);
      }
    };

    fetchGuestSubscriptionUnits();
  }, [userProfile?.guestSubscriptions]);

  // Fetch platform unit types to decide whether each unit shows a unit number
  useEffect(() => {
    const fetchUnitTypes = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
        if (settingsDoc.exists()) {
          setUnitTypes((settingsDoc.data().unitTypes || []) as UnitType[]);
        }
      } catch (error) {
        console.error('Error fetching unit types:', error);
      }
    };

    fetchUnitTypes();
  }, []);

  const handleLeaveUnit = async (unitId: string, unitName: string) => {
    if (!currentUser || !userProfile) return;

    // Check if user is the only member with Secretary role
    const unit = unitDetails[unitId];
    if (unit) {
      const memberships = userProfile.units || userProfile.units;
      const membership = memberships?.find((l: any) => ('unitId' in l ? l.unitId : l.lodgeId) === unitId);
      if (membership?.role === 'Secretary') {
        const secretaries = unit.members.filter((m) => m.role === 'Secretary');
        if (secretaries.length === 1) {
          showError('You cannot leave the unit as you are the only Secretary. Please assign another Secretary first.');
          return;
        }
      }
    }

    const result = await confirmAction({
      title: 'Leave Unit?',
      text: `Are you sure you want to leave ${unitName}? You will lose access to all events and information for this unit. This action cannot be undone.`,
      confirmButtonText: 'Leave Unit',
      icon: 'warning',
    });

    if (!result.isConfirmed) {
      return;
    }

    setSaving(true);
    try {
      // Find the membership object to remove
      const memberships = userProfile.units || userProfile.units;
      const membershipToRemove = memberships?.find((l: any) => ('unitId' in l ? l.unitId : l.lodgeId) === unitId);
      
      if (!membershipToRemove) {
        throw new Error('Membership not found');
      }

      // Remove from user's units array
      await updateDoc(doc(db, 'users', currentUser.uid), {
        units: arrayRemove(membershipToRemove),
      });

      // Remove from unit's members array
      const unitDoc = await getDoc(doc(db, 'units', unitId));
      if (unitDoc.exists()) {
        const unitData = unitDoc.data();
        const memberToRemove = unitData.members?.find((m: any) => m.uid === currentUser.uid);
        
        if (memberToRemove) {
          await updateDoc(doc(db, 'units', unitId), {
            members: arrayRemove(memberToRemove),
          });
        }
      }

      // Refresh user profile and unit context
      await refreshUserProfile();
      await refreshUnits();

      showSuccess(`You have left ${unitName}`);
      
      // Remove from local state
      const newDetails = { ...unitDetails };
      delete newDetails[unitId];
      setUnitDetails(newDetails);
    } catch (error) {
      console.error('Error leaving unit:', error);
      showError('Failed to leave unit. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      showError('Please fill in all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      showError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      showError('Password must be at least 6 characters long');
      return;
    }

    const result = await confirmAction({
      title: 'Change Password?',
      text: 'You will need to log in again with your new password.',
    });

    if (!result.isConfirmed) {
      return;
    }

    setSaving(true);
    try {
      // TODO: Implement password change
      // Note: Firebase requires re-authentication before changing password
      showError('Password change functionality coming soon');
    } catch (error) {
      console.error('Error changing password:', error);
      showError('Failed to change password');
    } finally {
      setSaving(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  const handleEnable2FA = () => {
    setShow2FASetup(true);
  };

  const handle2FASetupComplete = async (secret: string, backupCodes: string[]) => {
    if (!currentUser) return;

    setSaving(true);
    try {
      // Get device info and add as trusted device
      const { getDeviceInfo, generateDeviceId } = await import('@/lib/totp');
      const deviceInfo = getDeviceInfo();
      const deviceId = generateDeviceId();
      
      const newDevice = {
        id: deviceId,
        name: deviceInfo.name,
        userAgent: deviceInfo.userAgent,
        lastUsed: new Date(),
        createdAt: new Date(),
      };

      await updateDoc(doc(db, 'users', currentUser.uid), {
        twoFactorEnabled: true,
        twoFactorSecret: secret,
        twoFactorBackupCodes: backupCodes,
        trustedDevices: [newDevice], // Add current device as first trusted device
      });

      await refreshUserProfile();
      setShow2FASetup(false);
      showSuccess('Two-factor authentication enabled successfully!');
    } catch (error) {
      console.error('Error enabling 2FA:', error);
      showError('Failed to enable two-factor authentication');
    } finally {
      setSaving(false);
    }
  };

  const handleDisable2FA = () => {
    setShowVerifyDisable(true);
  };

  const handleVerifyDisable2FA = async () => {
    if (!currentUser || !userProfile?.twoFactorSecret || !verifyCode) {
      showError('Please enter the verification code');
      return;
    }

    // Check if it's a backup code
    const isBackupCode = verifyCode.includes('-');
    let isValid = false;

    if (isBackupCode) {
      isValid = userProfile.twoFactorBackupCodes?.includes(verifyCode) || false;
    } else {
      const { verifyTOTPCode } = await import('@/lib/totp');
      isValid = verifyTOTPCode(userProfile.twoFactorSecret, verifyCode);
    }

    if (!isValid) {
      showError('Invalid code. Please try again.');
      return;
    }

    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        twoFactorEnabled: false,
        twoFactorSecret: null,
        twoFactorBackupCodes: null,
      });

      await refreshUserProfile();
      setShowVerifyDisable(false);
      setVerifyCode('');
      showSuccess('Two-factor authentication disabled');
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      showError('Failed to disable two-factor authentication');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDevice = async (deviceId: string) => {
    if (!currentUser) return;

    const result = await confirmAction({
      title: 'Remove Device?',
      text: 'This device will need to verify 2FA again on next login.',
    });

    if (!result.isConfirmed) return;

    setSaving(true);
    try {
      const deviceToRemove = userProfile?.trustedDevices?.find(d => d.id === deviceId);
      if (deviceToRemove) {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          trustedDevices: arrayRemove(deviceToRemove),
        });
        await refreshUserProfile();
        showSuccess('Device removed successfully');
      }
    } catch (error) {
      console.error('Error removing device:', error);
      showError('Failed to remove device');
    } finally {
      setSaving(false);
    }
  };

  const handleUnsubscribeFromGuests = async (unitId: string, unitName: string) => {
    if (!currentUser || !userProfile) return;

    const result = await confirmAction({
      title: 'Unsubscribe from Guest List?',
      text: `You will no longer receive invitations to future events from ${unitName}. You can re-subscribe by RSVPing to an event and opting in again.`,
      confirmButtonText: 'Unsubscribe',
      icon: 'warning',
    });

    if (!result.isConfirmed) {
      return;
    }

    setSaving(true);
    try {
      // Remove from user's guest subscriptions
      const guestSubscriptions = userProfile.guestSubscriptions || [];
      const updatedSubscriptions = guestSubscriptions.filter((s: any) => s.unitId !== unitId);
      
      await updateDoc(doc(db, 'users', currentUser.uid), {
        guestSubscriptions: updatedSubscriptions,
      });

      // Remove from unit's guest list
      const unitDoc = await getDoc(doc(db, 'units', unitId));
      if (unitDoc.exists()) {
        const unitData = unitDoc.data();
        const guestList = unitData.guestList || [];
        const updatedGuestList = guestList.filter(
          (g: any) => g.userId !== currentUser.uid && g.email !== userProfile.email
        );
        
        await updateDoc(doc(db, 'units', unitId), {
          guestList: updatedGuestList,
        });
      }

      // Refresh user profile
      await refreshUserProfile();

      showSuccess(`You have unsubscribed from ${unitName}'s guest list`);
      
      // Remove from local state
      const newGuestUnits = { ...guestSubscriptionUnits };
      delete newGuestUnits[unitId];
      setGuestSubscriptionUnits(newGuestUnits);
    } catch (error) {
      console.error('Error unsubscribing:', error);
      showError('Failed to unsubscribe. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleInitiateEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser?.email) {
      showError('Unable to retrieve current email address');
      return;
    }

    if (!newEmail) {
      showError('Please enter a new email address');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      showError('Please enter a valid email address');
      return;
    }

    if (newEmail.toLowerCase() === currentUser.email.toLowerCase()) {
      showError('New email must be different from your current email');
      return;
    }

    const result = await confirmAction({
      title: 'Change Email Address?',
      text: `You are about to change your email address to: ${newEmail}. We will send verification emails to both your current and new email addresses to confirm this change.`,
      confirmButtonText: 'Continue',
    });

    if (!result.isConfirmed) {
      return;
    }

    setChangingEmail(true);
    try {
      // Call callable function (handles auth)
      const initiateEmailChange = httpsCallable(cloudFunctions, 'initiateEmailChange');
      const result = await initiateEmailChange({ newEmail });
      const data: any = result.data;

      if (data?.success) {
        showSuccess('Verification email sent! Please check your current email address to authorize this change.');
        setShowEmailChange(false);
        setNewEmail('');
        await refreshUserProfile();
      } else {
        // Fallback to HTTP endpoint with CORS
        throw new Error(data?.message || 'callable_failed');
      }
    } catch (err) {
      try {
        // Fallback: direct HTTP to CORS-enabled endpoint
        const idToken = await currentUser?.getIdToken();
        const isEmulator = import.meta.env.MODE === 'development';
        const baseUrl = isEmulator
          ? 'http://localhost:5001/demo-masonic-tools/europe-west2'
          : 'https://europe-west2-masonic-tools.cloudfunctions.net';

        const response = await fetch(`${baseUrl}/initiateEmailChangeHttp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify({ newEmail }),
        });

        const data = await response.json();

        if (data?.success) {
          showSuccess('Verification email sent! Please check your current email address to authorize this change.');
          setShowEmailChange(false);
          setNewEmail('');
          await refreshUserProfile();
        } else {
          showError(data?.message || 'Failed to initiate email change');
        }
      } catch (fallbackError) {
        console.error('Error initiating email change (fallback):', fallbackError);
        showError('Failed to initiate email change. Please try again.');
      }
    } finally {
      setChangingEmail(false);
    }
  };

  const handleCancelEmailChange = async () => {
    if (!currentUser) return;

    const result = await confirmAction({
      title: 'Cancel Email Change?',
      text: 'This will cancel the pending email change request.',
      confirmButtonText: 'Cancel Request',
    });

    if (!result.isConfirmed) {
      return;
    }

    setSaving(true);
    try {
      const cancelEmailChange = httpsCallable(cloudFunctions, 'cancelEmailChange');
      const result = await cancelEmailChange({});
      const data: any = result.data;

      if (data?.success) {
        showSuccess('Email change request cancelled');
        await refreshUserProfile();
      } else {
        throw new Error(data?.message || 'callable_failed');
      }
    } catch (err) {
      try {
        const idToken = await currentUser.getIdToken();
        const isEmulator = import.meta.env.MODE === 'development';
        const baseUrl = isEmulator
          ? 'http://localhost:5001/demo-masonic-tools/europe-west2'
          : 'https://europe-west2-masonic-tools.cloudfunctions.net';

        const response = await fetch(`${baseUrl}/cancelEmailChangeHttp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
        });
        const data = await response.json();
        if (data?.success) {
          showSuccess('Email change request cancelled');
          await refreshUserProfile();
        } else {
          showError(data?.message || 'Failed to cancel email change');
        }
      } catch (fallbackError) {
        console.error('Error cancelling email change (fallback):', fallbackError);
        showError('Failed to cancel email change');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!userProfile) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-4xl">
          {/* Header */}
          <div className="mb-8">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
            <div className="mt-4">
              <h1 className="text-3xl font-bold">Personal Settings</h1>
              <p className="text-muted-foreground">Manage your account and preferences</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-6 border-b">
            <div className="flex space-x-4">
              <button
                onClick={() => setActiveTab('profile')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 border-b-2 font-medium transition-colors',
                  activeTab === 'profile'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <User className="h-4 w-4" />
                Profile
              </button>
              <button
                onClick={() => setActiveTab('units')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 border-b-2 font-medium transition-colors',
                  activeTab === 'units'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <Building className="h-4 w-4" />
                Memberships
                {((userProfile.units || userProfile.units)?.length || 0) > 0 && (
                  <Badge variant="default" className="ml-1">
                    {(userProfile.units || userProfile.units)?.length || 0}
                  </Badge>
                )}
              </button>
              <button
                onClick={() => setActiveTab('guests')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 border-b-2 font-medium transition-colors',
                  activeTab === 'guests'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <Mail className="h-4 w-4" />
                Guest Subscriptions
                {userProfile.guestSubscriptions && userProfile.guestSubscriptions.length > 0 && (
                  <Badge variant="default" className="ml-1">
                    {userProfile.guestSubscriptions.length}
                  </Badge>
                )}
              </button>
              <button
                onClick={() => setActiveTab('security')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 border-b-2 font-medium transition-colors',
                  activeTab === 'security'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <Shield className="h-4 w-4" />
                Security
              </button>
            </div>
          </div>

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Personal Information</CardTitle>
                  <CardDescription>
                    Update your personal details and RSVP form defaults. Unit affiliations here are for
                    forms only; platform membership is managed under the Memberships tab.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <ProfileSetup
                    isModal={false}
                    onComplete={() => {
                      // Profile updated successfully
                    }}
                  />
                  
                  {/* Email Address Section */}
                  <div className="border-t pt-6">
                    <div className="space-y-4">
                      <div>
                        <Label>Email Address</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm">{currentUser?.email || userProfile.email}</span>
                          {!userProfile.pendingEmailChange && !showEmailChange && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowEmailChange(true)}
                              className="ml-2"
                            >
                              Change Email
                            </Button>
                          )}
                        </div>
                        
                        {/* Pending Email Change Notice */}
                        {userProfile.pendingEmailChange && (
                          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-start gap-2">
                              <Mail className="h-4 w-4 text-blue-600 mt-0.5" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-blue-900">
                                  Email change in progress
                                </p>
                                <p className="text-xs text-blue-700 mt-1">
                                  New email: <strong>{userProfile.pendingEmailChange.newEmail}</strong>
                                </p>
                                {!userProfile.pendingEmailChange.currentEmailVerified && (
                                  <p className="text-xs text-blue-700 mt-1">
                                    ⏳ Waiting for authorization from your current email address
                                  </p>
                                )}
                                {userProfile.pendingEmailChange.currentEmailVerified && !userProfile.pendingEmailChange.newEmailVerified && (
                                  <p className="text-xs text-blue-700 mt-1">
                                    ⏳ Waiting for verification from your new email address
                                  </p>
                                )}
                                <p className="text-xs text-blue-600 mt-1">
                                  Expires: {formatDate(userProfile.pendingEmailChange.expiresAt)}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleCancelEmailChange}
                                disabled={saving}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Email Change Form */}
                        {showEmailChange && !userProfile.pendingEmailChange && (
                          <form onSubmit={handleInitiateEmailChange} className="mt-3 p-4 border rounded-lg space-y-3">
                            <div>
                              <Label htmlFor="newEmail">New Email Address</Label>
                              <Input
                                id="newEmail"
                                type="email"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                placeholder="Enter new email address"
                                className="mt-1"
                                disabled={changingEmail}
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                We will send verification emails to both your current and new email addresses.
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button type="submit" disabled={changingEmail} size="sm">
                                {changingEmail ? 'Sending...' : 'Send Verification Email'}
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  setShowEmailChange(false);
                                  setNewEmail('');
                                }}
                                size="sm"
                              >
                                Cancel
                              </Button>
                            </div>
                          </form>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

            </div>
          )}

          {/* Memberships Tab */}
          {activeTab === 'units' && (
            <Card>
              <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
                <div>
                  <CardTitle>Memberships</CardTitle>
                  <CardDescription>Orders, Lodges & Chapters you are a member of</CardDescription>
                </div>
                <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/unit/join')}
                  >
                    <UserPlus className="mr-2 h-4 w-4" />
                    Join Unit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/unit/create')}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create New Unit
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingUnits ? (
                  <div className="text-center py-8">
                    <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
                    <p className="text-muted-foreground">Loading units...</p>
                  </div>
                ) : ((userProfile.units || userProfile.units)?.length || 0) > 0 ? (
                  <div className="space-y-3">
                    {(userProfile.units || userProfile.units || []).map((membership: any, index: number) => {
                      const unitId = 'unitId' in membership ? membership.unitId : membership.lodgeId;
                      const unit = unitDetails[unitId];
                      return (
                        <div key={index} className="flex items-center justify-between rounded-lg border p-4 hover:bg-muted/50 transition-colors">
                          <div className="flex-1">
                            <div className="font-semibold">
                              {unit ? `${unit.name}${unit.unitNumber && unitDisplaysNumber(unit.typeId, unitTypes) ? ` (${unit.unitNumber})` : ''}` : `Unit ID: ${unitId}`}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Joined {formatDate(membership.joinedAt)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge>{membership.role}</Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleLeaveUnit(
                                unitId,
                                unit?.name || 'this unit'
                              )}
                              disabled={saving}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              title="Leave Unit"
                            >
                              <LogOut className="h-4 w-4 mr-1" />
                              Leave
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Building className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>You are not a member of any orders, lodges or chapters yet</p>
                    <p className="text-sm mt-1">
                      Join an existing lodge or chapter, create your own, or wait for an invitation
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Guest Subscriptions Tab */}
          {activeTab === 'guests' && (
            <Card>
              <CardHeader>
                <CardTitle>Guest Subscriptions</CardTitle>
                <CardDescription>
                  Units you're subscribed to as a guest for future event invitations
                </CardDescription>
              </CardHeader>
              <CardContent>
                {userProfile.guestSubscriptions && userProfile.guestSubscriptions.length > 0 ? (
                  <div className="space-y-3">
                    {userProfile.guestSubscriptions.map((subscription: any, index: number) => {
                      const unit = guestSubscriptionUnits[subscription.unitId];
                      return (
                        <div key={index} className="flex items-center justify-between rounded-lg border p-4 hover:bg-muted/50 transition-colors">
                          <div className="flex-1">
                            <div className="font-semibold">
                              {unit ? `${unit.name}${unit.unitNumber && unitDisplaysNumber(unit.typeId, unitTypes) ? ` (${unit.unitNumber})` : ''}` : `Unit ID: ${subscription.unitId}`}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Subscribed {formatDate(subscription.subscribedAt)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              Guest
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUnsubscribeFromGuests(
                                subscription.unitId,
                                unit?.name || 'this unit'
                              )}
                              disabled={saving}
                              className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                              title="Unsubscribe"
                            >
                              <Mail className="h-4 w-4 mr-1" />
                              Unsubscribe
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Mail className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>You are not subscribed to any unit guest lists</p>
                    <p className="text-sm mt-1">
                      Subscribe by checking "Invite me to future events" when RSVPing to an event
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Change Password</CardTitle>
                  <CardDescription>Update your password to keep your account secure</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="currentPassword">Current Password</Label>
                      <Input
                        id="currentPassword"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="newPassword">New Password</Label>
                      <Input
                        id="newPassword"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={saving}
                      />
                      <p className="text-xs text-muted-foreground">
                        Must be at least 6 characters long
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirm New Password</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <Button type="submit" disabled={saving}>
                      {saving ? 'Updating...' : 'Change Password'}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Two-Factor Authentication</CardTitle>
                  <CardDescription>
                    Add an extra layer of security to your account
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">Two-Factor Authentication</div>
                      <div className="text-sm text-muted-foreground">
                        {userProfile.twoFactorEnabled
                          ? 'Your account is protected with 2FA'
                          : 'Protect your account with an authenticator app'}
                      </div>
                    </div>
                    <Badge variant={userProfile.twoFactorEnabled ? 'success' : 'default'}>
                      {userProfile.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  
                  {!userProfile.twoFactorEnabled && (
                    <Button variant="outline" onClick={handleEnable2FA} disabled={saving}>
                      <Shield className="h-4 w-4 mr-2" />
                      Enable Two-Factor Authentication
                    </Button>
                  )}
                  
                  {userProfile.twoFactorEnabled && !showVerifyDisable && (
                    <>
                      <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-sm text-green-900">
                        ✓ Your account is more secure with two-factor authentication enabled.
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          Backup codes remaining: {userProfile.twoFactorBackupCodes?.length || 0}
                        </p>
                        <Button variant="outline" onClick={handleDisable2FA} disabled={saving} className="text-destructive">
                          Disable Two-Factor Authentication
                        </Button>
                      </div>
                    </>
                  )}
                  
                  {showVerifyDisable && (
                    <div className="space-y-4 p-4 border rounded-lg">
                      <div>
                        <Label htmlFor="verifyCode">Enter code to disable 2FA</Label>
                        <Input
                          id="verifyCode"
                          type="text"
                          placeholder="000000 or XXXX-XXXX"
                          value={verifyCode}
                          onChange={(e) => setVerifyCode(e.target.value.toUpperCase())}
                          className="mt-2"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Enter your authenticator code or a backup code
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleVerifyDisable2FA} disabled={saving || !verifyCode}>
                          {saving ? 'Verifying...' : 'Verify & Disable'}
                        </Button>
                        <Button variant="outline" onClick={() => {
                          setShowVerifyDisable(false);
                          setVerifyCode('');
                        }}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Trusted Devices */}
              {userProfile.twoFactorEnabled && (
                <Card>
                  <CardHeader>
                    <CardTitle>Trusted Devices</CardTitle>
                    <CardDescription>
                      Manage devices that have access to your account with 2FA
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {userProfile.trustedDevices && userProfile.trustedDevices.length > 0 ? (
                      <div className="space-y-3">
                        {userProfile.trustedDevices.map((device) => (
                          <div key={device.id} className="flex items-center justify-between rounded-lg border p-4 hover:bg-muted/50 transition-colors">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-primary/10 rounded-lg">
                                {device.name.toLowerCase().includes('mobile') || device.name.toLowerCase().includes('android') || device.name.toLowerCase().includes('ios') ? (
                                  <Smartphone className="h-5 w-5 text-primary" />
                                ) : (
                                  <Monitor className="h-5 w-5 text-primary" />
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="font-medium">{device.name}</div>
                                <div className="text-sm text-muted-foreground">
                                  Last used: {formatDate(device.lastUsed)}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  Added: {formatDate(device.createdAt)}
                                </div>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveDevice(device.id)}
                              disabled={saving}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Remove
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>No trusted devices yet</p>
                        <p className="text-sm mt-1">
                          Devices will appear here after successfully verifying 2FA
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </main>

      {/* 2FA Setup Modal */}
      {show2FASetup && currentUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="my-8">
            <TwoFactorSetup
              userEmail={currentUser.email || ''}
              onComplete={handle2FASetupComplete}
              onCancel={() => setShow2FASetup(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

